import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Megaphone, Plus, Sparkles, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useTrialLimits } from "@/hooks/useTrialLimits";
import { TrialLimitBanner } from "@/components/TrialLimitBanner";
import { useI18n } from "@/lib/i18n";
import { usePlanUsage } from "@/hooks/usePlanUsage";
import { usePlanAccess } from "@/hooks/usePlanAccess";
import { UpgradePromptDialog } from "@/components/UpgradePromptDialog";

type Campaign = {
  id: string;
  name: string;
  platform: string;
  status: string;
  created_at: string;
  budget_type: string | null;
  daily_budget: number | null;
  total_budget: number | null;
  duration_days: number | null;
};

type SpendByCampaign = Record<string, number>;

function totalBudgetFor(c: Campaign): number {
  if (c.budget_type === "daily") return (c.daily_budget ?? 0) * (c.duration_days ?? 0);
  return c.total_budget ?? 0;
}

function formatBudget(c: Campaign): string {
  if (!c.budget_type) return "—";
  if (c.budget_type === "daily" && c.daily_budget) {
    const total = (c.daily_budget ?? 0) * (c.duration_days ?? 0);
    return `$${Number(c.daily_budget).toLocaleString()}/day · ${c.duration_days ?? 0}d ($${total.toLocaleString()})`;
  }
  return `$${Number(c.total_budget ?? 0).toLocaleString()} total · ${c.duration_days ?? 0}d`;
}

export const Route = createFileRoute("/_app/campaigns")({
  component: CampaignsPage,
  head: () => ({
    meta: [
      { title: "Campaigns · AdKavi" },
      { name: "description", content: "All your ad campaigns in one place." },
    ],
  }),
});

function CampaignsPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { isLocked } = useTrialLimits();
  const lockedNew = isLocked("campaign");
  const { reached, limit, counts } = usePlanUsage();
  const { requiredPlanFor } = usePlanAccess();
  const planCapReached = reached("campaigns");
  const campaignLimit = limit("campaigns");
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showNoAccount, setShowNoAccount] = useState(false);
  const [hasConnection, setHasConnection] = useState<boolean | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [spendMap, setSpendMap] = useState<SpendByCampaign>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const [campaignsData, meta, google, tiktok] = await Promise.all([
        supabase
          .from("campaigns")
          .select("id, name, platform, status, created_at, budget_type, daily_budget, total_budget, duration_days")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false }),
        supabase.from("meta_connections").select("user_id").eq("user_id", user.id).limit(1),
        supabase.from("google_connections").select("user_id").eq("user_id", user.id).limit(1),
        supabase.from("tiktok_connections").select("user_id").eq("user_id", user.id).limit(1),
      ]);
      if (cancelled) return;
      if (campaignsData.error) console.error(campaignsData.error);
      const list = (campaignsData.data as Campaign[]) ?? [];
      setCampaigns(list);
      setHasConnection(
        (meta.data?.length ?? 0) + (google.data?.length ?? 0) + (tiktok.data?.length ?? 0) > 0,
      );

      // Aggregate spend per campaign
      const { data: metricsData } = await supabase
        .from("campaign_metrics")
        .select("campaign_id, spend")
        .eq("user_id", user.id)
        .limit(1000);
      if (cancelled) return;
      const map: SpendByCampaign = {};
      for (const m of (metricsData as { campaign_id: string; spend: number }[] | null) ?? []) {
        map[m.campaign_id] = (map[m.campaign_id] ?? 0) + (Number(m.spend) || 0);
      }
      setSpendMap(map);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user]);

  function handleCreate() {
    if (hasConnection === false) {
      setShowNoAccount(true);
      return;
    }
    navigate({ to: "/campaigns/new" });
  }

  const rows = useMemo(() => campaigns.map((c) => {
    const budget = totalBudgetFor(c);
    const spend = spendMap[c.id] ?? 0;
    const pct = budget > 0 ? Math.min(100, (spend / budget) * 100) : 0;
    return { c, budget, spend, pct };
  }), [campaigns, spendMap]);

  return (
    <section className="max-w-6xl mx-auto px-6 py-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3">
            <Megaphone className="w-8 h-8 text-orange-400" />
            <span className="bg-gradient-to-r from-orange-400 to-primary bg-clip-text text-transparent">{t("campaignsList.title")}</span>
          </h1>
          <p className="mt-2 text-muted-foreground">{t("campaignsList.subtitle")}</p>
        </div>
        {lockedNew ? (
          <Button disabled title={t("campaignsList.trialLimit")}>
            <Sparkles className="w-4 h-4 mr-2" /> {t("campaignsList.newAi")}
          </Button>
        ) : planCapReached ? (
          <Button variant="secondary" onClick={() => setShowUpgrade(true)}>
            <Lock className="w-4 h-4 mr-2" /> {t("campaignsList.newAi")}
          </Button>
        ) : (
          <Button onClick={handleCreate}>
            <Sparkles className="w-4 h-4 mr-2" /> {t("campaignsList.newAi")}
          </Button>
        )}
      </div>

      {planCapReached && campaignLimit !== null && (
        <div className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm flex items-center justify-between gap-3 flex-wrap">
          <span>
            {t("plan.limit.campaignsReached", {
              count: String(counts.campaigns),
              limit: String(campaignLimit),
            })}
          </span>
          <Button size="sm" variant="secondary" onClick={() => setShowUpgrade(true)}>
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {t("plan.upgradePrompt.upgrade")}
          </Button>
        </div>
      )}

      <UpgradePromptDialog
        open={showUpgrade}
        onOpenChange={setShowUpgrade}
        title={t("plan.limit.campaignsTitle")}
        requiredPlan={requiredPlanFor("campaigns") === "Launch" ? "Scale" : requiredPlanFor("campaigns")}
        description={
          campaignLimit !== null
            ? t("plan.limit.campaignsDesc", { limit: String(campaignLimit) })
            : undefined
        }
      />


      <TrialLimitBanner kind="campaign" />

      <Dialog open={showNoAccount} onOpenChange={setShowNoAccount}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-primary" /> {t("campaignsList.connect.title")}
            </DialogTitle>
            <DialogDescription>
              {t("campaignsList.connect.desc")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowNoAccount(false)}>
              {t("campaignsList.connect.cancel")}
            </Button>
            <Button asChild onClick={() => setShowNoAccount(false)}>
              <Link to="/settings" search={{ tab: "integrations" }}>
                {t("campaignsList.connect.btn")}
              </Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="rounded-2xl border border-border/50 bg-card/40 p-10 text-center text-muted-foreground">
          {t("campaignsList.loading")}
        </div>
      ) : campaigns.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border/60 bg-card/30 p-12 text-center">
          <Megaphone className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <h3 className="text-lg font-semibold">{t("campaignsList.empty.title")}</h3>
          <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
            {t("campaignsList.empty.body")}
          </p>
          <Button className="mt-6" onClick={handleCreate}>
            <Plus className="w-4 h-4 mr-2" /> {t("campaignsList.empty.cta")}
          </Button>
        </div>
      ) : (
        <div className="rounded-2xl border border-border/50 bg-card/40 backdrop-blur overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="text-xs uppercase text-muted-foreground bg-background/30">
              <tr>
                <th className="text-left px-4 sm:px-6 py-3 font-medium">{t("campaignsList.col.name")}</th>
                <th className="text-left px-3 sm:px-4 py-3 font-medium">{t("campaignsList.col.platform")}</th>
                <th className="text-left px-3 sm:px-4 py-3 font-medium">{t("campaignsList.col.budget")}</th>
                <th className="text-left px-3 sm:px-4 py-3 font-medium w-56">{t("campaignsList.col.spend")}</th>
                <th className="text-left px-3 sm:px-4 py-3 font-medium">{t("campaignsList.col.status")}</th>
                <th className="text-right px-4 sm:px-6 py-3 font-medium">{t("campaignsList.col.created")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ c, budget, spend, pct }) => (
                <tr key={c.id} className="border-t border-border/30 hover:bg-background/20">
                  <td className="px-4 sm:px-6 py-4 sm:py-3 font-medium">
                    <Link to="/campaigns/$campaignId" params={{ campaignId: c.id }} className="hover:text-primary inline-flex items-center min-h-11">
                      {c.name}
                    </Link>
                  </td>
                  <td className="px-3 sm:px-4 py-4 sm:py-3 capitalize text-muted-foreground">{c.platform}</td>
                  <td className="px-3 sm:px-4 py-4 sm:py-3 text-muted-foreground whitespace-nowrap">{formatBudget(c)}</td>
                  <td className="px-3 sm:px-4 py-4 sm:py-3">
                    {budget > 0 ? (
                      <div className="space-y-1">
                        <Progress value={pct} className="h-1.5" />
                        <div className="flex justify-between text-[11px] text-muted-foreground">
                          <span>${spend.toFixed(0)} / ${budget.toLocaleString()}</span>
                          <span>{pct.toFixed(0)}%</span>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 sm:px-4 py-4 sm:py-3">
                    <Badge variant={c.status === "active" ? "default" : "secondary"}>
                      {t(`campaignsList.status.${c.status}`) || c.status}
                    </Badge>
                  </td>
                  <td className="px-4 sm:px-6 py-4 sm:py-3 text-right text-muted-foreground whitespace-nowrap">
                    {new Date(c.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </section>
  );
}
